import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ROLE_PROFESSOR } from '../../constants/roles';
import useUserMenu from '../../menus/useUserMenu';

const TEXT = '#F2F5FB';
const TEXT_MUTE = '#5C6B88';

function TabIcon({ name, active }) {
    switch (name) {
        case 'home':
            return (
                <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                    <path d="M3.5 9.5L11 3.5l7.5 6v8.5a1 1 0 01-1 1h-4v-6h-5v6h-4a1 1 0 01-1-1V9.5z"
                          stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
                          fill={active ? 'rgba(255,255,255,0.12)' : 'none'}/>
                </svg>
            );
        case 'wallet':
            return (
                <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                    <rect x="3" y="6" width="16" height="12" rx="2.5" stroke="currentColor" strokeWidth="1.6"
                          fill={active ? 'rgba(255,255,255,0.12)' : 'none'}/>
                    <path d="M3 9.5h13a3 3 0 010 6H3" stroke="currentColor" strokeWidth="1.6"/>
                    <circle cx="16" cy="12.5" r="1.1" fill="currentColor"/>
                </svg>
            );
        case 'markets':
            return (
                <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                    <path d="M3 14l4-5 3 3 4-7 5 9" stroke="currentColor" strokeWidth="1.6"
                          strokeLinejoin="round" strokeLinecap="round"/>
                    <path d="M3 18.5h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
            );
        case 'trophy':
            return (
                <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                    <path d="M7 4h8v5a4 4 0 11-8 0V4z" stroke="currentColor" strokeWidth="1.6"
                          strokeLinejoin="round" fill={active ? 'rgba(255,255,255,0.12)' : 'none'}/>
                    <path d="M5 5H3v2a2 2 0 002 2M17 5h2v2a2 2 0 01-2 2M9 17h4M11 13v4"
                          stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
            );
        case 'user':
            return (
                <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                    <circle cx="11" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6"
                            fill={active ? 'rgba(255,255,255,0.12)' : 'none'}/>
                    <path d="M4 19c0-3.6 3.1-6 7-6s7 2.4 7 6"
                          stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
            );
        case 'teams':
            return (
                <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                    <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.6"
                            fill={active ? 'rgba(255,255,255,0.12)' : 'none'}/>
                    <circle cx="15" cy="9" r="2.4" stroke="currentColor" strokeWidth="1.6"
                            fill={active ? 'rgba(255,255,255,0.12)' : 'none'}/>
                    <path d="M3 18c0-3 2.2-5 5-5s5 2 5 5M14 18c0-2.2 1.5-4 4-4"
                          stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
            );
        case 'video':
            return (
                <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                    <rect x="3" y="6" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.6"
                          fill={active ? 'rgba(255,255,255,0.12)' : 'none'}/>
                    <path d="M15 9.5l4-2.5v8l-4-2.5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/>
                </svg>
            );
        case 'document':
            return (
                <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                    <path d="M6 3h7l4 4v12a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z"
                          stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"
                          fill={active ? 'rgba(255,255,255,0.12)' : 'none'}/>
                    <path d="M8 11h6M8 14.5h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
            );
        case 'admin':
            return (
                <svg width={22} height={22} viewBox="0 0 22 22" fill="none">
                    <rect x="3" y="6" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.6"
                          fill={active ? 'rgba(255,255,255,0.12)' : 'none'}/>
                    <path d="M3 9.5h16M7 6V4.5a1 1 0 011-1h6a1 1 0 011 1V6" stroke="currentColor" strokeWidth="1.6"/>
                </svg>
            );
        default:
            return <div style={{ width: 22, height: 22 }}/>;
    }
}

function TabItem({ label, icon, active, onClick }) {
    return (
        <button onClick={onClick} style={{
            background: 'none', border: 'none', padding: '6px 0 4px',
            cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            color: active ? TEXT : TEXT_MUTE,
            transition: 'color 0.15s ease',
        }}>
            <TabIcon name={icon} active={active}/>
            <span style={{
                fontSize: 10, fontWeight: active ? 600 : 500,
                letterSpacing: '0.01em',
            }}>{label}</span>
        </button>
    );
}

function CenterButton({ onClick }) {
    return (
        <button onClick={onClick} aria-label="Investir" style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            display: 'grid', placeItems: 'center',
        }}>
            <div style={{
                width: 50, height: 50, borderRadius: '50%',
                background: 'linear-gradient(180deg, #ffffff 0%, #d8e0ec 100%)',
                display: 'grid', placeItems: 'center',
                marginLeft: 6,
                marginTop: -4,
                boxShadow: '0 0 0 4px rgba(20,33,61,0.6), inset 0 1px 0 rgba(255,255,255,0.8), 0 8px 20px rgba(0,0,0,0.45)',
            }}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M10 4v12M4 10h12" stroke="#0A1226" strokeWidth="2.2" strokeLinecap="round"/>
                </svg>
            </div>
        </button>
    );
}

const Navbarfooter = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const path = location.pathname;
    const role = useSelector((state) => state.auth.user?.role);
    const menuLinks = useUserMenu();

    // Les professeurs n'ont pas de menu fixed en bas d'écran sur mobile
    if (role === ROLE_PROFESSOR) {
        return null;
    }

    return (
        <div className="lg:hidden" style={{
            position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 30,
            padding: '12px 14px 14px',
            pointerEvents: 'none',
        }}>
            <div style={{
                pointerEvents: 'auto',
                borderRadius: 28,
                background: 'rgba(14,24,48,0.85)',
                backdropFilter: 'blur(22px) saturate(180%)',
                WebkitBackdropFilter: 'blur(22px) saturate(180%)',
                border: '1px solid rgba(255,255,255,0.08)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 32px rgba(0,0,0,0.5)',
                padding: '8px 20px 6px 16px',
            }} className={`flex ${menuLinks.length < 4 ? 'justify-around' : 'justify-between'}`}>
                {menuLinks.map(({ path: linkPath, label, icon, isActive }) => (
                    <TabItem
                        key={linkPath}
                        label={label}
                        icon={icon}
                        active={isActive(path)}
                        onClick={() => navigate(linkPath)}
                    />
                ))}
            </div>
        </div>
    );
};

export default Navbarfooter;
